/**
 * The generic experiment-arm stamp for buy links: `xa=<flag>:<variant>`,
 * comma-joined when a visitor is in more than one experiment, stored on the
 * click as `link_clicks.metadata.exp_arms` (an object keyed by flag). It lets
 * commission be split by arm for any experiment listed here, so a new test
 * adds one allowlist entry instead of a new one-off param like `swv` or `arv`.
 *
 * `xa` is a public query param, so the api is the trust boundary: it keeps
 * only pairs whose flag AND variant are in `EXPERIMENT_ARM_ALLOWLIST`, which
 * also caps the entries per click at one per listed flag. The web stamps from
 * the same list, so the two sides cannot drift.
 *
 * One comma-joined value rather than repeated `xa=` params: the api's
 * double-wrap unwrap keeps one value per key, so repeats would collapse.
 *
 * ⛔ Variant strings are join keys in revenue readouts. Renaming one splits an
 * experiment's clicks across two names; add a new value instead.
 */
import { FeatureFlags } from './constants'

export const EXPERIMENT_ARM_PARAM = 'xa'

export const EXPERIMENT_ARM_ALLOWLIST = {
  [FeatureFlags.BrowseHomeBand]: ['control', 'test'],
  [FeatureFlags.BrowseHoldout]: ['holdout', 'shop'],
} as const satisfies Partial<Record<FeatureFlags, readonly string[]>>

export type ExperimentArmFlag = keyof typeof EXPERIMENT_ARM_ALLOWLIST
export type ExperimentArms = Partial<Record<ExperimentArmFlag, string>>

/** Far above any legitimate value; rejects crafted input before splitting. */
const MAX_RAW_LENGTH = 256

export function isExperimentArm(
  flag: string,
  variant: unknown
): flag is ExperimentArmFlag {
  if (!Object.hasOwn(EXPERIMENT_ARM_ALLOWLIST, flag)) return false
  const variants: readonly string[] =
    EXPERIMENT_ARM_ALLOWLIST[flag as ExperimentArmFlag]
  return typeof variant === 'string' && variants.includes(variant)
}

/** Narrows an untrusted `xa` value to allowlisted pairs. Unknown flags,
 *  unknown variants and malformed entries are dropped; the first value wins
 *  for a repeated flag. Returns null when nothing survives. */
export function parseExperimentArms(raw: unknown): ExperimentArms | null {
  if (typeof raw !== 'string' || !raw || raw.length > MAX_RAW_LENGTH) {
    return null
  }
  const arms: ExperimentArms = {}
  for (const entry of raw.split(',')) {
    const sep = entry.indexOf(':')
    if (sep <= 0) continue
    const flag = entry.slice(0, sep)
    const variant = entry.slice(sep + 1)
    if (isExperimentArm(flag, variant) && !(flag in arms)) {
      arms[flag] = variant
    }
  }
  return Object.keys(arms).length > 0 ? arms : null
}

/** Builds the `xa` value from arms, keeping only allowlisted pairs, in a
 *  stable order. Returns null when nothing is left to stamp. */
export function serializeExperimentArms(
  arms: Readonly<Record<string, string | null | undefined>>
): string | null {
  const entries = Object.keys(arms)
    .sort()
    .flatMap((flag) => {
      const variant = arms[flag]
      return isExperimentArm(flag, variant) ? [`${flag}:${variant}`] : []
    })
  return entries.length > 0 ? entries.join(',') : null
}
